import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Sparkle,
  Warning,
  ArrowUpRight,
  ClockCounterClockwise,
  CaretDown,
  CaretRight,
  User as UserIcon,
  Briefcase,
  NoteBlank,
  Heartbeat,
} from "@phosphor-icons/react";

/**
 * ClientHealthBrief — Iter 85.12 · executive brief on a client, not a
 * metrics dashboard.
 *
 * Renders the /health-brief endpoint's structured output: a short
 * headline plus 3–4 narrative sections, each with an inline collapsible
 * evidence block that lists (and deep-links to) the specific
 * engagements / notes / tasks / relationship signals the paragraph was
 * grounded in.
 *
 * The read is the story. Evidence is one click away.
 */
export default function ClientHealthBrief({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/health-brief`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load brief");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) load();
    // eslint-disable-next-line
  }, [clientId]);

  const regen = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/health-brief`);
      setData(data);
      toast.success("Brief refreshed");
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div
        className="border border-border bg-card p-8 text-xs text-muted-foreground"
        data-testid="client-health-brief-loading"
      >
        Reading the room…
      </div>
    );
  }
  if (!data) return null;

  const sections = data.sections || [];
  const hasSections = sections.length > 0;

  return (
    <div className="space-y-6" data-testid="client-health-brief">
      {/* Provisional banner — same tone as Storyline, so partners never
          confuse this with a final judgment. */}
      <div
        className="border-l-2 border-amber-500/70 bg-amber-500/5 px-4 py-3 flex items-start gap-3"
        data-testid="client-health-brief-banner"
      >
        <Warning size={16} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-amber-500">
            Provisional executive brief
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            A partner's read on where this partner organization sits right now. Every claim expands into the specific evidence it was grounded in.
          </div>
        </div>
        <Button
          onClick={regen}
          disabled={regenerating}
          size="sm"
          variant="outline"
          className="rounded-none border-primary/60 text-primary hover:bg-primary/10 shrink-0"
          data-testid="client-health-brief-regen"
        >
          <Sparkle size={12} weight={regenerating ? "regular" : "fill"} className="mr-1.5" />
          {regenerating ? "Reading…" : "Refresh"}
        </Button>
      </div>

      {/* Headline — one sentence, editorial voice. */}
      {data.headline && (
        <div
          className="border border-border bg-card px-6 py-5"
          data-testid="client-health-brief-headline"
        >
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Current read
          </div>
          <p className="font-display text-xl leading-snug tracking-tight text-foreground">
            {data.headline}
          </p>
        </div>
      )}

      {hasSections ? (
        <div className="space-y-4">
          {sections.map((s, i) => (
            <BriefSection key={i} section={s} idx={i} clientId={clientId} />
          ))}
        </div>
      ) : (
        <div
          className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground"
          data-testid="client-health-brief-empty"
        >
          Not enough grounding to draft a brief. Capture a meeting note or update the relationship health, then Refresh.
        </div>
      )}

      <div
        className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground pt-2 border-t border-border"
        data-testid="client-health-brief-meta"
      >
        Generated{" "}
        {new Date(data.generated_at).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })}
      </div>
    </div>
  );
}

function BriefSection({ section, idx, clientId }) {
  const [open, setOpen] = useState(false);
  const ev = section.evidence_hydrated || {};
  const totalCitations =
    (ev.engagements?.length || 0) +
    (ev.notes?.length || 0) +
    (ev.tasks?.length || 0) +
    (ev.contacts?.length || 0) +
    (ev.health_transitions?.length || 0);

  return (
    <div
      className="border border-border bg-card"
      data-testid={`client-health-brief-section-${idx}`}
    >
      <div className="px-6 pt-5 pb-3">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">
          {section.heading}
        </div>
        <p className="font-serif text-base leading-relaxed text-foreground mt-2">
          {section.paragraph}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={totalCitations === 0}
        className="w-full border-t border-border px-6 py-2 text-left flex items-center justify-between hover:bg-secondary/40 disabled:hover:bg-transparent disabled:opacity-60"
        data-testid={`client-health-brief-section-${idx}-toggle`}
      >
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
          Evidence · {totalCitations}
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/80">
          {totalCitations === 0 ? "no grounding" : open ? "hide" : "show"}
        </div>
      </button>
      {open && totalCitations > 0 && (
        <div
          className="px-6 py-4 border-t border-border bg-secondary/20 space-y-3"
          data-testid={`client-health-brief-section-${idx}-evidence`}
        >
          {ev.engagements?.length > 0 && (
            <EvidenceGroup label="Engagements" icon={Briefcase}>
              {ev.engagements.map((e) => (
                <Link
                  key={e.id}
                  to={`/engagements/${e.id}`}
                  className="block text-xs hover:text-primary"
                  data-testid={`client-health-brief-evidence-eng-${e.id}`}
                >
                  <div className="font-medium truncate">{e.name}</div>
                  <div className="font-mono uppercase tracking-widest text-muted-foreground text-[0.625rem]">
                    {e.stage} · {e.status} · health {e.health}
                    <ArrowUpRight size={10} weight="bold" className="inline ml-1" />
                  </div>
                </Link>
              ))}
            </EvidenceGroup>
          )}
          {ev.notes?.length > 0 && (
            <EvidenceGroup label="Notes" icon={NoteBlank}>
              {ev.notes.map((n) => {
                const to = n.engagement_id
                  ? `/engagements/${n.engagement_id}?tab=notes&openNote=${n.id}`
                  : `/clients/${clientId}?tab=notes&openNote=${n.id}`;
                return (
                  <Link
                    key={n.id}
                    to={to}
                    className="block text-xs hover:text-primary"
                    data-testid={`client-health-brief-evidence-note-${n.id}`}
                  >
                    <div className="font-medium truncate">{n.title}</div>
                    <div className="font-mono uppercase tracking-widest text-muted-foreground text-[0.625rem]">
                      {n.note_type} · {(n.meeting_date || n.created_at || "").slice(0, 10)}
                      <ArrowUpRight size={10} weight="bold" className="inline ml-1" />
                    </div>
                  </Link>
                );
              })}
            </EvidenceGroup>
          )}
          {ev.tasks?.length > 0 && (
            <EvidenceGroup label="Overdue tasks" icon={ClockCounterClockwise}>
              {ev.tasks.map((t) => (
                <Link
                  key={t.id}
                  to={
                    t.engagement_id
                      ? `/engagements/${t.engagement_id}?tab=tasks&openTask=${t.id}`
                      : `/tasks?open=${t.id}`
                  }
                  className="block text-xs hover:text-primary"
                  data-testid={`client-health-brief-evidence-task-${t.id}`}
                >
                  <div className="font-medium truncate">{t.title}</div>
                  <div className="font-mono uppercase tracking-widest text-muted-foreground text-[0.625rem]">
                    overdue since {t.due_date}
                    <ArrowUpRight size={10} weight="bold" className="inline ml-1" />
                  </div>
                </Link>
              ))}
            </EvidenceGroup>
          )}
          {ev.contacts?.length > 0 && (
            <EvidenceGroup label="Relationship signals" icon={UserIcon}>
              {ev.contacts.map((c) => (
                <Link
                  key={c.id}
                  to={`/clients/${clientId}?tab=stakeholders&openContact=${c.id}`}
                  className="block text-xs hover:text-primary"
                  data-testid={`client-health-brief-evidence-contact-${c.id}`}
                >
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="font-mono uppercase tracking-widest text-muted-foreground text-[0.625rem]">
                    {c.days_since === null ? "never touched" : `${c.days_since}d silent`}
                    {c.posture ? ` · ${c.posture}` : ""}
                    <ArrowUpRight size={10} weight="bold" className="inline ml-1" />
                  </div>
                </Link>
              ))}
            </EvidenceGroup>
          )}
          {ev.health_transitions?.length > 0 && (
            <EvidenceGroup label="Health transitions" icon={Heartbeat}>
              {ev.health_transitions.map((t, j) => (
                <div key={j} className="text-xs" data-testid={`client-health-brief-evidence-transition-${j}`}>
                  <span className="font-mono uppercase tracking-widest text-muted-foreground">
                    {(t.date || "").slice(0, 10)}
                  </span>
                  <span className="ml-2 text-foreground">
                    {t.from} → <span className="font-medium">{t.to}</span>
                  </span>
                  {t.changed_by && (
                    <span className="ml-1 text-muted-foreground">· by {t.changed_by}</span>
                  )}
                </div>
              ))}
            </EvidenceGroup>
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceGroup({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1.5 flex items-center gap-1">
        {Icon && <Icon size={10} weight="bold" />} {label}
      </div>
      <div className="space-y-1.5 pl-3 border-l border-border/60">{children}</div>
    </div>
  );
}
